import React, { useMemo, useState } from 'react';
import { FaShoppingCart, FaCheck, FaPlus, FaMinus } from 'react-icons/fa';
import AlreadyInCartSheet from '../../components/AlreadyInCartSheet/AlreadyInCartSheet';
import { useCart } from '../../context/CartContext';
import { useStoreStatus } from '../../context/StoreStatusContext';
import {
  buildCartLineFromVariant,
  getCartLinesForMenuItem,
  getCartQtyForMenuItem,
  getLatestCartLineForMenuItem,
  isVariableMenuItem,
  menuItemHasAddOns,
  menuItemIsCustomisable,
  menuItemNeedsDetailBeforeAdd,
} from './ourMenuUtils';
import {
  applyAddOnsToCartLine,
  getCartLineTotal,
  resolveCartLineKey,
} from './cartAddOnUtils';

const ADD_LABEL = {
  card: ' ADD TO CART',
  list: ' ADD TO CART',
  color: ' ADD TO CART',
  compact: '',
  modal: ' ADD TO CART',
};

const ADDED_LABEL = {
  card: ' ADDED',
  list: ' ADDED',
  color: ' ADDED',
  compact: '',
  modal: ' ADDED TO CART',
};

function buildEditOptions(item, line) {
  if (!line) return { intent: 'cart', mode: 'new' };
  const addOns = Array.isArray(line.addOns) ? line.addOns : [];
  /** @type {Record<number, number>} */
  const initialAddOnQtys = {};
  for (const a of addOns) {
    const id = Number(a.id ?? a.addOnItemID);
    if (!Number.isFinite(id) || id <= 0) continue;
    initialAddOnQtys[id] = Math.max(1, Math.round(Number(a.quantity) || 1));
  }
  return {
    intent: 'cart',
    mode: 'edit',
    editCartKey: resolveCartLineKey(line),
    initialVariantId:
      item && isVariableMenuItem(item) ? Number(line.id) : null,
    initialAddOnIds: Object.keys(initialAddOnQtys).map(Number),
    initialAddOnQtys,
    initialQuantity: Math.max(1, Math.round(Number(line.quantity) || 1)),
  };
}

/**
 * Add-to-cart CTA that switches to cart-style +/- quantity when the item is already in the cart.
 * Modal: uses draftQuantity + Add/Update (no live cart stepper — qty is chosen before commit).
 * When add-ons are involved and the item is already in cart, + / customise opens the 3-option sheet.
 */
export default function MenuCartAction({
  item,
  orderable = true,
  unavailableLabel = 'Unavailable',
  variant = 'card',
  className = '',
  addedClassName = '',
  onAfterAdd,
  onOpenOptions,
  selectedVariant = null,
  selectedAddOns = [],
  editCartKey = null,
  /** Plate count chosen in the details modal (Add / Update). */
  draftQuantity = 1,
}) {
  const { cartItems, addToCart, updateQty, replaceCartLine, addedId } = useCart();
  const { confirmAddWhileClosed } = useStoreStatus();
  const [sheetOpen, setSheetOpen] = useState(false);

  const variable = isVariableMenuItem(item);
  const customisable = menuItemIsCustomisable(item);
  const needsDetail =
    typeof onOpenOptions === 'function' &&
    menuItemNeedsDetailBeforeAdd(item, { selectedVariant });
  const isEditMode = Boolean(editCartKey);
  const onCardSurface = variant !== 'modal';
  const plateQty = Math.max(1, Math.round(Number(draftQuantity) || 1));

  const basePayload =
    selectedVariant
      ? buildCartLineFromVariant(item, selectedVariant)
      : variable
        ? null
        : {
            id: item.id,
            name: item.name,
            price: item.price,
            priceDisplay: item.priceDisplay,
            image: item.image,
            category: item.category,
          };

  const cartPayload = basePayload
    ? applyAddOnsToCartLine(
        {
          ...basePayload,
          hostUnitPrice:
            basePayload.hostUnitPrice != null
              ? basePayload.hostUnitPrice
              : basePayload.price,
          price:
            basePayload.hostUnitPrice != null
              ? basePayload.hostUnitPrice
              : basePayload.price,
        },
        selectedAddOns,
      )
    : null;

  const cartLineKey = cartPayload ? resolveCartLineKey(cartPayload) : null;
  const cartLine =
    cartLineKey != null
      ? (cartItems || []).find((row) => resolveCartLineKey(row) === cartLineKey)
      : null;
  const exactQty = Math.max(0, Number(cartLine?.quantity) || 0);

  const familyLines = useMemo(
    () => getCartLinesForMenuItem(cartItems, item),
    [cartItems, item],
  );
  const familyQty = onCardSurface
    ? getCartQtyForMenuItem(cartItems, item)
    : exactQty;
  const latestFamilyLine = onCardSurface
    ? getLatestCartLineForMenuItem(cartItems, item)
    : cartLine;

  const quantity = onCardSurface && customisable ? familyQty : exactQty;
  const justAdded =
    addedId != null &&
    ((cartLineKey != null && String(addedId) === String(cartLineKey)) ||
      (latestFamilyLine &&
        String(addedId) === String(resolveCartLineKey(latestFamilyLine))));

  const familyHasAddOns = familyLines.some(
    (l) => Array.isArray(l.addOns) && l.addOns.length > 0,
  );
  const useAlreadySheet =
    onCardSurface &&
    typeof onOpenOptions === 'function' &&
    familyLines.length > 0 &&
    (menuItemHasAddOns(item) || familyHasAddOns);

  const openSheet = () => setSheetOpen(true);
  const closeSheet = () => setSheetOpen(false);

  const addToRegularCart = async () => {
    if (needsDetail) {
      if (useAlreadySheet) {
        openSheet();
        return;
      }
      onOpenOptions(item, { intent: 'cart', mode: 'new' });
      return;
    }
    if (!cartPayload) return;
    const ok = await confirmAddWhileClosed();
    if (!ok) return;
    addToCart(cartPayload, {
      quantity: variant === 'modal' ? plateQty : 1,
    });
    if (typeof onAfterAdd === 'function') onAfterAdd();
  };

  const saveEdit = async () => {
    if (!cartPayload || !editCartKey) return;
    const ok = await confirmAddWhileClosed();
    if (!ok) return;
    replaceCartLine(editCartKey, cartPayload, { quantity: plateQty });
    if (typeof onAfterAdd === 'function') onAfterAdd();
  };

  const increaseExact = async () => {
    if (cartLineKey == null) return;
    const ok = await confirmAddWhileClosed();
    if (!ok) return;
    updateQty(cartLineKey, exactQty + 1);
  };

  const increaseFamily = async () => {
    if (useAlreadySheet) {
      openSheet();
      return;
    }
    if (latestFamilyLine) {
      const ok = await confirmAddWhileClosed();
      if (!ok) return;
      updateQty(
        resolveCartLineKey(latestFamilyLine),
        Math.max(0, Number(latestFamilyLine.quantity) || 0) + 1,
      );
      return;
    }
    await increaseExact();
  };

  const decreaseFamily = () => {
    const target = latestFamilyLine || cartLine;
    if (!target) return;
    const key = resolveCartLineKey(target);
    const qty = Math.max(0, Number(target.quantity) || 0);
    updateQty(key, qty - 1);
  };

  const handleAddPlate = async () => {
    const target = latestFamilyLine;
    if (!target) return;
    const ok = await confirmAddWhileClosed();
    if (!ok) return;
    updateQty(
      resolveCartLineKey(target),
      Math.max(0, Number(target.quantity) || 0) + 1,
    );
    closeSheet();
  };

  const handleEdit = () => {
    const line = latestFamilyLine;
    closeSheet();
    if (typeof onOpenOptions !== 'function' || !line) return;
    window.requestAnimationFrame(() => {
      onOpenOptions(item, buildEditOptions(item, line));
    });
  };

  const handleNew = () => {
    closeSheet();
    if (typeof onOpenOptions !== 'function') return;
    window.requestAnimationFrame(() => {
      onOpenOptions(item, { intent: 'cart', mode: 'new' });
    });
  };

  const sheet =
    sheetOpen && latestFamilyLine ? (
      <AlreadyInCartSheet
        itemName={item.name}
        lastLine={latestFamilyLine}
        familyLines={familyLines}
        onAddPlate={handleAddPlate}
        onEdit={handleEdit}
        onNew={handleNew}
        onClose={closeSheet}
      />
    ) : null;

  if (!orderable) {
    return (
      <button
        type="button"
        className={`${className} menu-item__cta--oos`.trim()}
        disabled
        aria-disabled="true"
      >
        {unavailableLabel}
      </button>
    );
  }

  if (isEditMode) {
    const editTotal =
      cartPayload != null
        ? getCartLineTotal({ ...cartPayload, quantity: plateQty })
        : null;
    return (
      <button
        type="button"
        className={`${className}`.trim()}
        onClick={saveEdit}
        aria-label={`Update ${item.name} in cart`}
      >
        <FaCheck />
        {variant === 'modal' && editTotal != null
          ? ` UPDATE CART · ₹${editTotal.toFixed(0)}`
          : variant === 'modal'
            ? ' UPDATE CART'
            : ' UPDATE'}
      </button>
    );
  }

  // Card / list: show family qty stepper once anything for this product is in cart.
  if (onCardSurface && quantity > 0) {
    return (
      <>
        <div
          className={`menu-qty-stepper menu-qty-stepper--cart menu-qty-stepper--${variant}`}
          role="group"
          aria-label={`Regular cart quantity for ${item.name}`}
          title="Regular cart"
        >
          <button
            type="button"
            className="menu-qty-stepper__btn"
            onClick={(e) => {
              e.stopPropagation();
              decreaseFamily();
            }}
            aria-label={`Decrease cart quantity of ${item.name}`}
          >
            <FaMinus />
          </button>
          <span className="menu-qty-stepper__value" aria-live="polite">
            <span className="menu-qty-stepper__kind">
              <FaShoppingCart aria-hidden />
              <span className="menu-qty-stepper__kind-text">Cart</span>
            </span>
            <span className="menu-qty-stepper__count">{quantity}</span>
          </span>
          <button
            type="button"
            className="menu-qty-stepper__btn"
            onClick={(e) => {
              e.stopPropagation();
              increaseFamily();
            }}
            aria-label={`Increase cart quantity of ${item.name}`}
          >
            <FaPlus />
          </button>
        </div>
        {sheet}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`${className}${justAdded && addedClassName ? ` ${addedClassName}` : ''}`.trim()}
        onClick={(e) => {
          e.stopPropagation();
          addToRegularCart();
        }}
        aria-label={
          justAdded
            ? 'Added to cart'
            : needsDetail
              ? `Add ${item.name} to cart — open details`
              : `Add ${item.name} to cart`
        }
      >
        {justAdded ? (
          <>
            <FaCheck />
            {ADDED_LABEL[variant] ?? ADDED_LABEL.card}
          </>
        ) : (
          <>
            <FaShoppingCart />
            {variant === 'modal' && plateQty > 1
              ? ` ADD ${plateQty}`
              : ADD_LABEL[variant] ?? ADD_LABEL.card}
          </>
        )}
      </button>
      {sheet}
    </>
  );
}
